import {
  Button,
  Checkbox,
  Modal,
  Popover,
  Tooltip,
  message,
} from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { useMemo, useState } from 'react';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';
import { EAssetType } from '../../asset/types/asset.type';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { httpRequest } from '../../../helpers/api';
import { CapturePdfParams } from '../types/report-log.type';
import {
  capitalizeFirstLetter,
  generateQueryString,
} from '@qlibs/react-components';
import { generateDeviceOptions } from './helperFunction';
import { EDeviceType } from '../../device/types/device.type';
import useMasterData from '../../../hooks/useMasterData';
import { APP_THEME } from '../../../const/config';
import useReportLog from '../../../hooks/useReportLog';
import { getErrorMessage, getErrorStatus } from '../../../helpers/httpError';
import { resolvePdfDownloadErrorModal } from './downloadPdfError';
import usePdfDownloadCooldown from '../../../hooks/usePdfDownloadCooldown';
import useReportDownload from '../../../zustand/useReportDownload';
import {
  clearPdfDownloadCooldown,
  getPdfDownloadCooldownRemaining,
  isPdfDownloadCooldownRange,
  startPdfDownloadCooldown,
} from '../../../helpers/pdfDownloadCooldown';
import { getReportRangeError } from '../helpers/reportIntervalLimit';

const dateFormat = 'DD MMM YYYY HH-mm';

const PopupDownload: React.FC<{
  loading: boolean;
  isReportLog?: boolean;
  dataLogColumns?: any;
  dataSource?: any;
}> = (props) => {
  const [selectedItems, setSelectedItems] = useState<CheckboxValueType[]>([]);
  const { filter, asset, devices, timezone } = useAppliedReportFilter();
  const [loading, setLoading] = useState<boolean>(false);
  const { isOperationMode } = useMasterData();
  const { distanceUnit, speedUnit, cruiseUnit } = useReportLog();
  const { isCoolingDown: hasActiveCooldown, remainingLabel } =
    usePdfDownloadCooldown();
  const isCooldownRange = isPdfDownloadCooldownRange(
    filter.startAt,
    filter.endAt
  );
  const isCoolingDown = isCooldownRange && hasActiveCooldown;
  const { activeDownload, startDownload, finishDownload } = useReportDownload();
  const isDownloadBusy = activeDownload !== null;

  const isOperationModeEnabled =
    asset.metadata?.showOperationMode === 1 || isOperationMode;

  function handleChange(checkedValues: CheckboxValueType[]) {
    setSelectedItems(checkedValues);
  }

  function buildCurrentFilter(dataDownloaded: string[]) {
    let currentDeviceType = filter.deviceType || '';
    const shouldIncludeOperationMode =
      isOperationMode ||
      isOperationModeEnabled ||
      dataDownloaded.includes('operation_mode');

    if (shouldIncludeOperationMode && !currentDeviceType.includes('operation_mode')) {
      currentDeviceType = currentDeviceType
        ? `${currentDeviceType},operation_mode`
        : 'operation_mode';
    }

    return {
      ...filter,
      deviceType: currentDeviceType,
    };
  }

  async function onDownloadPDF(datas: string[]) {
    const rangeError = getReportRangeError({ ...filter, timezone });

    if (rangeError) {
      message.error(rangeError);
      return;
    }

    if (!startDownload('pdf')) {
      return;
    }

    try {
      setLoading(true);
      if (isCooldownRange) {
        startPdfDownloadCooldown();
      }
      const newFilter = buildCurrentFilter(datas);

      const query: CapturePdfParams = {
        ...newFilter,
        interval: newFilter.interval || filter.interval,
        assetName: asset.name.split(' ').join('_'),
        scheduleReport: 1,
        dataDownloaded: datas,
        dataType:
          APP_THEME === 'ramus'
            ? 0
            : APP_THEME === 'pinisi'
            ? 1
            : APP_THEME === 'netra'
            ? 2
            : APP_THEME === 'bki'
            ? 3
            : APP_THEME === 'arnantara'
            ? 4
            : 0,
        distanceUnit,
        speedUnit,
        cruiseUnit,
        // Forward monthly-minute opt-in into generate-report-pdf → /report-pdf.
        ...(newFilter.allowWeeklyMinute === true
          ? { allowWeeklyMinute: true }
          : {}),
      };
      const res: any = await httpRequest.get(
        `/report-log/generate-report-pdf${generateQueryString(query)}`,
        {
          responseType: 'blob',
          timeout: 15 * 60 * 1000,
        }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const startAt = dayjs(filter.startAt * 1000)
        .utcOffset(timezone * 60)
        .format(dateFormat);
      const endAt = dayjs(filter.endAt * 1000)
        .utcOffset(timezone * 60)
        .format(dateFormat);
      const fileName = `Report ${asset.name} ${startAt} ${endAt}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('failed generate report', error);
      // Failed attempts should not lock the user out for the full cooldown.
      if (isCooldownRange) {
        clearPdfDownloadCooldown();
      }

      const statusCode = getErrorStatus(error);
      const errorMessage = await getErrorMessage(error);
      const errorModal = resolvePdfDownloadErrorModal({
        statusCode,
        errorMessage,
      });

      if (statusCode === 400) {
        Modal.warning({
          title: errorModal.title,
          content: errorModal.content,
          okText: 'OK',
        });
        return;
      }

      message.error(errorModal.content);
    } finally {
      setLoading(false);
      finishDownload();
    }
  }

  async function handleDownload(all?: boolean) {
    if (loading || props.loading || isDownloadBusy) {
      return;
    }

    if (isCooldownRange && getPdfDownloadCooldownRemaining() > 0) {
      message.warning(
        `Please wait ${remainingLabel} before downloading another PDF.`
      );
      return;
    }

    if (!all) {
      await onDownloadPDF(selectedItems as string[]);
    } else if (options && options.length) {
      await onDownloadPDF(options.map((item) => item.key));
    }
  }

  const options = useMemo(() => {
    let res: { key: string; label: string; hide?: boolean }[] = [];

    if (asset && asset.assetType) {
      const availableDevices =
        Array.isArray(devices) && devices.length > 0
          ? devices
          : asset.devices || [];
      const reportOptions = generateDeviceOptions(availableDevices as any);
      const filterDeviceType = (filter.deviceType || '').toLowerCase();
      const hasOption = (key: string) => res.some((item) => item.key === key);

      res.push({
        key: 'summary',
        label: 'Summary',
      });

      if (isOperationModeEnabled) {
        res.push({
          key: 'operation_mode',
          label: 'Operation Mode',
        });
      }

      reportOptions.forEach((item) => {
        if (item.type === EDeviceType.GPS) {
          res.push({
            key: 'vessel_tracking',
            label: 'Vessel Tracking',
          });
        } else if (item.type === EDeviceType.FLOWMETER) {
          res.push(
            {
              key:
                asset.assetType === EAssetType.EMS
                  ? 'rpm_speed_ae'
                  : 'rpm_fuel_speed_ae',
              label: 'RPM vs Fuel Cons. vs Speed vs AE Running Time',
            },
            {
              key: 'me_fuel_consumption',
              label: 'ME Fuel Consumption',
            }
          );
          item.devices.forEach((key) => {
            res.push({
              key: `${key}_fuel_consumption_running`,
              label: `${capitalizeFirstLetter(key)} Fuel Used vs Running Hours`,
            });
          });
        } else if (item.type === EDeviceType.AE) {
          res.push({
            key: 'ae_fuel_consumption',
            label: 'AE Fuel Consumption',
          });
        }
      });

      if (reportOptions.length === 0) {
        if (filterDeviceType.includes('gps') && !hasOption('vessel_tracking')) {
          res.push({
            key: 'vessel_tracking',
            label: 'Vessel Tracking',
          });
        }

        if (asset.assetType === EAssetType.EMS) {
          if (filterDeviceType.includes('rpm') && !hasOption('rpm_speed_ae')) {
            res.push({
              key: 'rpm_speed_ae',
              label: 'RPM vs Speed vs AE Running Time',
            });
          }
        } else if (
          (filterDeviceType.includes('flowmeter') ||
            filterDeviceType.includes('rpm')) &&
          !hasOption('rpm_fuel_speed_ae')
        ) {
          res.push(
            {
              key: 'rpm_fuel_speed_ae',
              label: 'RPM vs Fuel Cons. vs Speed vs AE Running Time',
            },
            {
              key: 'me_fuel_consumption',
              label: 'ME Fuel Consumption',
            }
          );

          if (filterDeviceType.includes('fm-1')) {
            res.push({
              key: 'port_fuel_consumption_running',
              label: 'Port Fuel Used vs Running Hours',
            });
          }

          if (filterDeviceType.includes('fm-2')) {
            res.push({
              key: 'starboard_fuel_consumption_running',
              label: 'Starboard Fuel Used vs Running Hours',
            });
          }

          if (filterDeviceType.includes('fm-3')) {
            res.push({
              key: 'center_fuel_consumption_running',
              label: 'Center Fuel Used vs Running Hours',
            });
          }
        }

        if (filterDeviceType.includes('ae') && !hasOption('ae_fuel_consumption')) {
          res.push({
            key: 'ae_fuel_consumption',
            label: 'AE Fuel Consumption',
          });
        }
      }
    }

    return res;
  }, [asset, devices, isOperationModeEnabled]);

  const CheckboxRender = () => {
    return (
      <>
        <div style={{ color: '#919191' }}>Choose report to download</div>
        {isCoolingDown && (
          <div style={{ color: '#faad14', marginTop: 4 }}>
            Reports longer than 5 days can be downloaded again in{' '}
            {remainingLabel}
          </div>
        )}
        {options && options.length && (
          <Checkbox.Group
            style={{ width: '100%' }}
            onChange={handleChange}
            value={selectedItems}
          >
            {options.map((item, idx) => (
              <Checkbox
                style={{
                  margin: 0,
                  width: '100%',
                  padding: '5px 0',
                  borderBottom: '1px solid #f1f1f1',
                }}
                key={idx}
                value={item.key}
              >
                {item.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <Tooltip
            style={{ flex: 1 }}
            title='Select one or two report to download'
          >
            <Button
              style={{ flex: 1 }}
              onClick={() => handleDownload()}
              disabled={
                props.loading ||
                loading ||
                isDownloadBusy ||
                isCoolingDown ||
                !selectedItems.length
              }
              loading={props.loading || loading}
              type='primary'
            >
              Download {selectedItems.length > 0 && `(${selectedItems.length})`}
            </Button>
          </Tooltip>
          <Button
            style={{ flex: 1 }}
            onClick={() => handleDownload(true)}
            disabled={
              props.loading || loading || isDownloadBusy || isCoolingDown
            }
            loading={props.loading || loading}
            type='primary'
          >
            Download All
          </Button>
        </div>
      </>
    );
  };

  return (
    <Popover
      content={<CheckboxRender />}
      trigger='hover'
      overlayInnerStyle={{
        width: 320,
      }}
      placement='bottomRight'
    >
      <Button
        disabled={props.loading || loading || isDownloadBusy}
        loading={props.loading || loading}
        color='default'
        type='primary'
        icon={<DownloadOutlined />}
      >
        {isCoolingDown && !loading ? `Download PDF` : 'Download PDF'}
      </Button>
    </Popover>
  );
};

export default PopupDownload;
